import React, { useContext, useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { Row, Col } from '@thd-olt-component-react/core-ui';
import { Input } from '@thd-olt-component-react/input';
import { Alert } from '@one-thd/sui-atomic-components';
import { JobPostingContext } from '../../../contexts/JobPostingContext';
import { useStore } from '../../../store';
import Field from '../../common/Field/Field';
import RequiredDropDown from '../../common/RequiredDropDown/RequiredDropDown';
import { SuggestedLocationInput } from '../../common/SuggestedLocationInput/SuggestedLocationInput';
import { jobPostingValidators } from '../../../utils/validations';
import { JOB_TITLE_CHAR_LIMIT } from '../../../utils/constants';

const CreateJobPostingDropDowns = ({ showErrors }) => {
  const { editedJobPosting, updateJobPostingField } = useContext(JobPostingContext);

  const [isLocationDirty, setIsLocationDirty] = useState(false);
  const [isTitleDirty, setIsTitleDirty] = useState(false);

  const [displayedMinAmount, setDisplayedMinAmount] = useState(`$${editedJobPosting?.payRange?.minAmount}`);
  const [displayedMaxAmount, setDisplayedMaxAmount] = useState(`$${editedJobPosting?.payRange?.maxAmount}`);
  const [isMinAmountDirty, setIsMinAmountDirty] = useState(false);
  const [isMaxAmountDirty, setIsMaxAmountDirty] = useState(false);
  const [minMaxError, setMinMaxError] = useState(false);
  const [payFieldBlurred, setPayFieldBlurred] = useState(false);

  useEffect(() => {
    if (!payFieldBlurred && !showErrors.draft && !showErrors.publish) return;
    const min = editedJobPosting?.payRange?.minAmount;
    const max = editedJobPosting?.payRange?.maxAmount;
    if (min && max) {
      setMinMaxError(min >= max);
    } else {
      setMinMaxError(false);
    }
  }, [editedJobPosting?.payRange?.minAmount, editedJobPosting?.payRange?.maxAmount, payFieldBlurred, showErrors.draft, showErrors.publish]);

  const {
    staticDataStore: { tradeTypes, employmentTypes, yearsOfExperiences, payFrequencies }
  } = useStore();

  const handleBlur = (displayedAmount, setDisplayedAmount) => {
    const numericValue = parseFloat(displayedAmount.replace(/[^0-9.]/g, ''));
    if (!Number.isNaN(numericValue)) {
      const displayedValue = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(numericValue);
      setDisplayedAmount(displayedValue);
    } else {
      setDisplayedAmount('$');
    }
  };

  const handleMinAmountBlur = () => {
    handleBlur(displayedMinAmount, setDisplayedMinAmount);
  };

  const handleMaxAmountBlur = () => {
    handleBlur(displayedMaxAmount, setDisplayedMaxAmount);
    setPayFieldBlurred(true);
  };

  return (
    <>
      <Row>
        <Col>
          <Field
            label="Job Title"
            required
            dirty={isTitleDirty}
            showError={(isTitleDirty || showErrors.draft || showErrors.publish) && !jobPostingValidators.title(editedJobPosting)}
          >
            <Input
              required
              validateOnBlur
              value={editedJobPosting?.title}
              maxLength={JOB_TITLE_CHAR_LIMIT}
              onChange={(newValue) => {
                setIsTitleDirty(true);
                updateJobPostingField('title', newValue);
              }}
              validation={() => isTitleDirty && jobPostingValidators.title(editedJobPosting)}
              errorText="Please provide a job title"
              name="jobTitle"
              placeholder={
                window.innerWidth <= 600
                  ? 'Name of this job opening'
                  : 'Name of this job opening, for example, "Entry Level Carpenter"'
              }
            />
          </Field>
        </Col>
      </Row>
      <Row>
        <Col xs="6">
          <Field label="Type of Employment" required>
            <RequiredDropDown
              header={editedJobPosting?.employmentType}
              options={
                !employmentTypes?.length ? [editedJobPosting?.employmentType] : employmentTypes
              }
              isValid={jobPostingValidators.employmentType(editedJobPosting) || (!showErrors.draft && !showErrors.publish)}
              errorText="Please select an employment type"
              testId="edit-job-employment-type-dropdown"
              handleChange={(newValue) => updateJobPostingField('employmentType', newValue)}
            />
          </Field>
        </Col>
        <Col xs={6}>
          <SuggestedLocationInput
            label="Location"
            locationText={editedJobPosting?.locationText}
            locationTextError={
              (isLocationDirty || showErrors.publish) && !jobPostingValidators.locationText(editedJobPosting)
            }
            validation={(value) => {
              jobPostingValidators.locationText({ locationText: value });
            }}
            setLocationTextError={() => isLocationDirty && !jobPostingValidators.locationText(editedJobPosting)}
            setLocationText={(newValue) => {
              setIsLocationDirty(true);
              updateJobPostingField('locationText', newValue);
            }}
          />
        </Col>
      </Row>
      <Row>
        <Col xs="6">
          <Field label="Trade Type" required>
            <RequiredDropDown
              header={editedJobPosting?.tradeType}
              options={!tradeTypes?.length ? [editedJobPosting?.tradeType] : tradeTypes}
              isValid={jobPostingValidators.tradeType(editedJobPosting) || (!showErrors.draft && !showErrors.publish)}
              errorText="Please select a trade type"
              testId="edit-job-trade-type-dropdown"
              handleChange={(newValue) => updateJobPostingField('tradeType', newValue)}
            />
          </Field>
        </Col>
        <Col xs="6">
          <Field label="Experience Level" required>
            <RequiredDropDown
              header={editedJobPosting?.yearsOfExperience}
              options={
                !yearsOfExperiences?.length
                  ? [editedJobPosting?.yearsOfExperience]
                  : yearsOfExperiences
              }
              isValid={jobPostingValidators.yearsOfExperience(editedJobPosting) || !showErrors.publish}
              errorText="Please select experience level"
              testId="edit-job-experience-dropdown"
              handleChange={(newValue) => updateJobPostingField('yearsOfExperience', newValue)}
            />
          </Field>
        </Col>
      </Row>
      <Row>
        <Col xs="6">
          <Field label="Pay Frequency" required>
            <RequiredDropDown
              header={editedJobPosting?.payFrequency}
              options={
                !payFrequencies?.length ? [editedJobPosting?.payFrequency] : payFrequencies
              }
              isValid={jobPostingValidators.payFrequency(editedJobPosting) || (!showErrors.draft && !showErrors.publish)}
              errorText="Please select a pay frequency"
              testId="edit-job-pay-freqency-dropdown"
              handleChange={(newValue) => updateJobPostingField('payFrequency', newValue)}
            />
          </Field>
        </Col>
        <Col xs="3">
          <Field
            label="Minimum Pay"
            required
            dirty={isMinAmountDirty}
            showError={(isMinAmountDirty || showErrors.draft || showErrors.publish) && !jobPostingValidators.minAmount(editedJobPosting)}
          >
            <Input
              required
              validateOnBlur
              value={displayedMinAmount}
              maxLength={JOB_TITLE_CHAR_LIMIT}
              onChange={(newValue) => {
                setIsMinAmountDirty(true);
                const numericValue = parseFloat(newValue.replace(/[^0-9.]/g, ''));
                if (!Number.isNaN(numericValue)) {
                  updateJobPostingField('payRange', { ...editedJobPosting.payRange, ...{ minAmount: numericValue } });
                } else {
                  updateJobPostingField('payRange', { ...editedJobPosting.payRange, ...{ minAmount: '' } });
                }
                setDisplayedMinAmount(newValue);
              }}
              onBlur={() => handleMinAmountBlur()}
              validation={() => isMinAmountDirty && jobPostingValidators.minAmount(editedJobPosting)}
              errorText="Please provide a number for minimum pay rate"
              name="minimum"
              placeholder=" Min pay rate"
            />
          </Field>
        </Col>
        <Col xs="3">
          <Field
            label="Maximum Pay"
            required={false}
            dirty={isMaxAmountDirty}
            showError={(isMaxAmountDirty || showErrors.draft || showErrors.publish) && !jobPostingValidators.maxAmount(editedJobPosting)}
          >
            <Input
              required={false}
              validateOnBlur
              value={displayedMaxAmount}
              maxLength={JOB_TITLE_CHAR_LIMIT}
              onChange={(newValue) => {
                setIsMaxAmountDirty(true);
                const numericValue = parseFloat(newValue.replace(/[^0-9.]/g, ''));
                if (!Number.isNaN(numericValue)) {
                  updateJobPostingField('payRange', { ...editedJobPosting.payRange, ...{ maxAmount: numericValue } });
                } else {
                  updateJobPostingField('payRange', { ...editedJobPosting.payRange, ...{ maxAmount: '' } });
                }
                setDisplayedMaxAmount(newValue);
              }}
              onFocus={() => setIsMaxAmountDirty(true)}
              onBlur={() => handleMaxAmountBlur()}
              validation={() => isMaxAmountDirty && jobPostingValidators.maxAmount(editedJobPosting)}
              errorText="Please provide a number for maximum pay"
              name="maximum"
              placeholder=" Max pay rate"
            />
          </Field>
        </Col>
      </Row>
      <Row>
        <Col xs="6" />
        <Col xs="6">
          {minMaxError && (<Alert status="error">Please make the maximum more than the minimum</Alert>)}
        </Col>
      </Row>
    </>
  );
};

export default observer(CreateJobPostingDropDowns);
